import React, { useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TextField,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Divider,
  Typography,
} from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  DriverFormData,
  syncDriver,
  driverSchema,
} from "@lib/api/mutations/driver";
import { AxiosError } from "axios";
import { Driver, DriverResponse } from "@lib/types/driver";
import useHandleError from "@lib/utils/errorHook";
import { getUnusedSlots } from "@lib/api/queries/drivers";

import Autocomplete from "@mui/material/Autocomplete";
import { Plus, X } from "lucide-react";
import Loading from "@components/Loading";

interface Props {
  syncDriverDialogOpen: boolean;
  setSyncDriverDialogOpen: (open: boolean) => void;
  refetch?: () => void;
  driverData?: Driver;
}

const SyncDriver: React.FC<Props> = ({
  syncDriverDialogOpen,
  setSyncDriverDialogOpen,
  driverData,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const {
    data: slots,
    isFetching,
    error,
  } = useQuery<string[]>({
    queryKey: ["unused-slots"],
    queryFn: () => getUnusedSlots(),
    enabled: syncDriverDialogOpen,
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<DriverFormData>({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      name: driverData?.name || "",
      code: driverData?.code || "",
      description: driverData?.description || "",
      phone: driverData?.phone || "",
      mobile_key: driverData?.mobile_key || "",
      exclusive: driverData?.exclusive || false,
      custom_fields: driverData?.custom_fields || [],
    },
  });

  const { mutate, isPending: isSyncingDriver } = useMutation({
    mutationFn: ({
      syncDriverParams,
      wialonDriverId,
    }: {
      syncDriverParams: DriverFormData;
      wialonDriverId: number;
    }) => syncDriver(syncDriverParams, wialonDriverId),
    onSuccess: (data: DriverResponse) => {
      showSnackbar(t("user.drivers.syncSuccess"), "success");

      setSyncDriverDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const handleSyncDriver = (syncDriverParams: DriverFormData) => {
    if (driverData) {
      mutate({
        syncDriverParams,
        wialonDriverId: driverData.driver_id,
      });
    }
  };

  useEffect(() => {
    if (driverData) {
      setValue("name", driverData.name);
      setValue("code", driverData.code);
      setValue("description", driverData.description);
      setValue("phone", driverData.phone);
      setValue("mobile_key", driverData.mobile_key);
      setValue("exclusive", driverData.exclusive);
      setValue("custom_fields", driverData.custom_fields);
    }
  }, [driverData, syncDriverDialogOpen]);

  // Field Array for dynamic custom fields
  const { fields, append, remove } = useFieldArray({
    control,
    name: "custom_fields",
  });

  return (
    <FormDialog
      title={t("user.drivers.sync")}
      open={syncDriverDialogOpen}
      onClose={() => {
        setSyncDriverDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleSyncDriver)}
      submitText={t("general.save")}
      isSubmitting={isSyncingDriver}
      submittingText={t("general.saving")}
    >
      {isFetching && <Loading />}
      {!isFetching && (
        <form noValidate>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.name")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.code")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.description")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.phone")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />
            <Controller
              name="mobile_key"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.mobileKey")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.mobile_key}
                  helperText={errors.mobile_key?.message}
                />
              )}
            />
            <Controller
              name="parameter_number"
              control={control}
              render={({ field }) => (
                <div>
                  <Autocomplete
                    {...field} // Spread the field props from react-hook-form
                    options={slots || ["30000"]} // Items to display in the dropdown
                    onChange={(_, value) => field.onChange(value)} // Handle the change
                    loading={isFetching} // Loading state
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("user.drivers.parameterNumber")}
                        variant="outlined"
                        error={!!errors.parameter_number} // Error state for validation
                        helperText={errors.parameter_number?.message} // Error message
                      />
                    )}
                  />
                </div>
              )}
            />
            <Divider />
            {/* Custom Fields Section */}
            <Box>
              <Typography variant="subtitle1">
                {t("user.drivers.customFields")}
              </Typography>
              {fields.map((item, index) => (
                <Box
                  key={item.id}
                  display="flex"
                  gap={2}
                  alignItems="center"
                  my={1}
                >
                  <Controller
                    name={`custom_fields.${index}.key`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("user.drivers.customFieldKey")}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name={`custom_fields.${index}.value`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("user.drivers.customFieldValue")}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />

                  <IconButton onClick={() => remove(index)} color="error">
                    <X />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={() => append({ key: "", value: "" })}
                startIcon={<Plus />}
              >
                {t("user.drivers.addCustomField")}
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </FormDialog>
  );
};

export default SyncDriver;
